import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  Alert,
  Button,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Container,
  LinearProgress,
  Tabs,
  Tab,
} from '@mui/material';
import { RichTextEditor } from './RichTextEditor';
import { PracticeQuestionViewer } from '../student/PracticeQuestionViewer';
import {
  CheckCircle as CheckIcon,
  School as SchoolIcon,
  Assignment as AssignmentIcon,
  EmojiObjects as LightbulbIcon,
  NavigateNext as NextIcon,
  NavigateBefore as PrevIcon,
  Home as HomeIcon,
  Edit as EditIcon,
  Quiz as QuizIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

interface LessonViewerProps {
  moduleTitle: string;
  units: any[];
  onComplete?: () => void;
  onExit?: () => void;
  isTeacherPreview?: boolean;
  moduleId?: string;
}

export const LessonViewer: React.FC<LessonViewerProps> = ({
  moduleTitle,
  units,
  onComplete,
  onExit,
  isTeacherPreview = false,
  moduleId,
}) => {
  const navigate = useNavigate();
  const [currentUnitIndex, setCurrentUnitIndex] = useState(0);
  const [showQuiz, setShowQuiz] = useState(false);
  const [showPractice, setShowPractice] = useState(false);
  const [quizAnswers, setQuizAnswers] = useState<Record<string, string>>({});
  const [showQuizResults, setShowQuizResults] = useState(false);
  const [quizScore, setQuizScore] = useState(0);
  const [completedUnits, setCompletedUnits] = useState<Set<number>>(new Set());
  const [quizTab, setQuizTab] = useState(0); // 0 = Unit Test, 1 = Practice Questions

  const currentUnit = units[currentUnitIndex];
  const isLastUnit = currentUnitIndex === units.length - 1;
  const hasQuiz = currentUnit?.test && currentUnit.test.questions?.length > 0;

  const handleNextUnit = () => {
    if (showQuiz) {
      // Moving from quiz to next unit
      const newCompleted = new Set(Array.from(completedUnits));
      newCompleted.add(currentUnitIndex);
      setCompletedUnits(newCompleted);
      setShowQuiz(false);
      setShowPractice(false);
      setShowQuizResults(false);
      setQuizAnswers({});

      if (isLastUnit) {
        if (onComplete) onComplete();
      } else {
        setCurrentUnitIndex(currentUnitIndex + 1);
        window.scrollTo(0, 0);
      }
    } else {
      // Moving from content to quiz
      if (hasQuiz) {
        setShowQuiz(true);
        setShowPractice(false);
        window.scrollTo(0, 0);
      } else {
        // No quiz, go directly to next unit
        const newCompleted = new Set(Array.from(completedUnits));
        newCompleted.add(currentUnitIndex);
        setCompletedUnits(newCompleted);
        if (isLastUnit) {
          if (onComplete) onComplete();
        } else {
          setCurrentUnitIndex(currentUnitIndex + 1);
          window.scrollTo(0, 0);
        }
      }
    }
  };

  const handlePrevUnit = () => {
    if (showQuiz || showPractice) {
      setShowQuiz(false);
      setShowPractice(false);
      window.scrollTo(0, 0);
    } else if (currentUnitIndex > 0) {
      setCurrentUnitIndex(currentUnitIndex - 1);
      setShowQuiz(false);
      setShowPractice(false);
      setShowQuizResults(false);
      setQuizAnswers({});
      window.scrollTo(0, 0);
    }
  };

  const handleAnswerChange = (questionId: string, answerId: string) => {
    setQuizAnswers({
      ...quizAnswers,
      [questionId]: answerId,
    });
  };

  const handleSubmitQuiz = () => {
    if (!currentUnit.test) return;

    let correctCount = 0;
    currentUnit.test.questions.forEach((question: any) => {
      const selectedAnswer = quizAnswers[question.question_id];
      const correctOption = question.options?.find((opt: any) => opt.is_correct);
      if (selectedAnswer === correctOption?.option_id) {
        correctCount++;
      }
    });

    const score = Math.round((correctCount / currentUnit.test.questions.length) * 100);
    setQuizScore(score);
    setShowQuizResults(true);
  };

  const handlePractice = () => {
    setShowPractice(true);
    setShowQuiz(false);
    window.scrollTo(0, 0);
  };

  const listItems = (value: any): any[] => (Array.isArray(value) ? value : []);

  const renderSupportList = (items: any[], renderItem: (item: any) => React.ReactNode) => (
    <List dense>
      {items.map((item, idx) => (
        <ListItem key={idx} sx={{ alignItems: 'flex-start' }}>
          <ListItemText primary={renderItem(item)} />
        </ListItem>
      ))}
    </List>
  );

  const supportText = (value: any): string => {
    if (Array.isArray(value)) return value.join('; ');
    return typeof value === 'string' ? value : '';
  };

  const renderTeacherSupports = () => {
    const content = currentUnit?.content;
    if (!isTeacherPreview || !content) return null;

    const teacherNotes = listItems(content.teacher_notes || content.teaching_notes?.tips);
    const vocabulary = listItems(content.vocabulary || content.lesson_vocabulary);
    const checks = listItems(content.checks_for_understanding);
    const misconceptions = listItems(content.common_misconceptions);
    const differentiation = content.differentiation || content.teaching_notes?.differentiation;
    const assessment = content.assessment;
    const hasDifferentiation =
      supportText(differentiation?.struggling_learners || differentiation?.below_grade) ||
      supportText(differentiation?.english_language_learners) ||
      supportText(differentiation?.advanced_learners || differentiation?.above_grade);
    const hasAssessment =
      supportText(assessment?.formative_checks) ||
      assessment?.exit_ticket ||
      supportText(assessment?.success_criteria);

    if (
      teacherNotes.length === 0 &&
      vocabulary.length === 0 &&
      checks.length === 0 &&
      misconceptions.length === 0 &&
      !hasDifferentiation &&
      !hasAssessment
    ) {
      return null;
    }

    return (
      <Card sx={{ mb: 4, boxShadow: 2, bgcolor: 'warning.50' }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'warning.dark' }}>
            Teacher Supports
          </Typography>
          <Divider sx={{ mb: 3 }} />

          {teacherNotes.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Notes
              </Typography>
              {renderSupportList(teacherNotes, (note) => note)}
            </Box>
          )}

          {vocabulary.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Vocabulary
              </Typography>
              {renderSupportList(vocabulary, (item) =>
                typeof item === 'string'
                  ? item
                  : `${item.term}: ${item.student_friendly_explanation || item.definition}`,
              )}
            </Box>
          )}

          {checks.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Checks for Understanding
              </Typography>
              {renderSupportList(checks, (check) => check)}
            </Box>
          )}

          {misconceptions.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Common Misconceptions
              </Typography>
              {renderSupportList(misconceptions, (misconception) => misconception)}
            </Box>
          )}

          {hasDifferentiation && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Differentiation
              </Typography>
              {[
                [
                  'Struggling learners',
                  differentiation?.struggling_learners || differentiation?.below_grade,
                ],
                ['English language learners', differentiation?.english_language_learners],
                [
                  'Advanced learners',
                  differentiation?.advanced_learners || differentiation?.above_grade,
                ],
              ].map(([label, value]: any) => {
                const text = supportText(value);
                return text ? (
                  <Typography key={label} variant="body2" paragraph>
                    <strong>{label}:</strong> {text}
                  </Typography>
                ) : null;
              })}
            </Box>
          )}

          {hasAssessment && (
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Assessment
              </Typography>
              {supportText(assessment?.formative_checks) && (
                <Typography variant="body2" paragraph>
                  <strong>Formative checks:</strong> {supportText(assessment.formative_checks)}
                </Typography>
              )}
              {assessment?.exit_ticket && (
                <Typography variant="body2" paragraph>
                  <strong>Exit ticket:</strong> {assessment.exit_ticket}
                </Typography>
              )}
              {supportText(assessment?.success_criteria) && (
                <Typography variant="body2">
                  <strong>Success criteria:</strong> {supportText(assessment.success_criteria)}
                </Typography>
              )}
            </Box>
          )}
        </CardContent>
      </Card>
    );
  };

  const renderSection = (section: any, index: number) => {
    return (
      <Box key={index} sx={{ mb: 4 }}>
        {section.title && (
          <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>
            {section.title}
          </Typography>
        )}
        <Typography variant="body1" paragraph sx={{ lineHeight: 1.8, fontSize: '1.1rem' }}>
          {section.content}
        </Typography>
        {section.key_points && section.key_points.length > 0 && (
          <Paper
            sx={{ p: 3, bgcolor: 'primary.50', borderLeft: 4, borderColor: 'primary.main', my: 3 }}
          >
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
              Key Points:
            </Typography>
            {section.key_points.map((point: string, idx: number) => (
              <Box key={idx} sx={{ display: 'flex', alignItems: 'start', mb: 1.5 }}>
                <CheckIcon sx={{ color: 'success.main', mr: 1.5, mt: 0.5, fontSize: 22 }} />
                <Typography variant="body1">{point}</Typography>
              </Box>
            ))}
          </Paper>
        )}
        {section.examples && section.examples.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
              Examples:
            </Typography>
            {section.examples.map((example: any, idx: number) => (
              <Paper
                key={idx}
                sx={{ p: 3, mb: 2, bgcolor: 'grey.50', borderLeft: 3, borderColor: 'info.main' }}
              >
                <Typography
                  variant="subtitle2"
                  gutterBottom
                  sx={{ color: 'info.main', fontWeight: 600 }}
                >
                  Example {idx + 1}: {example.title}
                </Typography>
                <Typography variant="body1">{example.description}</Typography>
              </Paper>
            ))}
          </Box>
        )}

        {renderTeacherSupports()}
      </Box>
    );
  };

  const renderContent = () => {
    if (!currentUnit) return null;

    return (
      <Box>
        {/* Introduction */}
        {currentUnit.content?.introduction && (
          <Card sx={{ mb: 4, boxShadow: 2 }}>
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <SchoolIcon sx={{ fontSize: 36, color: 'primary.main', mr: 2 }} />
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                  Introduction
                </Typography>
              </Box>
              <Divider sx={{ mb: 3 }} />
              <RichTextEditor
                content={
                  currentUnit.content.introduction.html ||
                  currentUnit.content.introduction.plain_text ||
                  ''
                }
                onChange={() => undefined}
                readOnly={true}
                minHeight={100}
              />
            </CardContent>
          </Card>
        )}

        {/* Module Vocabulary */}
        {currentUnit.content?.module_vocabulary &&
          currentUnit.content.module_vocabulary.length > 0 && (
            <Card sx={{ mb: 4, boxShadow: 2, bgcolor: 'info.50' }}>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'info.main' }}>
                  Key Vocabulary
                </Typography>
                <Divider sx={{ mb: 3 }} />
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                  {currentUnit.content.module_vocabulary.map((term: string, idx: number) => (
                    <Chip
                      key={idx}
                      label={term}
                      variant="outlined"
                      color="info"
                      sx={{ fontSize: '0.95rem', py: 2.5 }}
                    />
                  ))}
                </Box>
              </CardContent>
            </Card>
          )}

        {/* Lesson Vocabulary */}
        {currentUnit.content?.lesson_vocabulary &&
          currentUnit.content.lesson_vocabulary.length > 0 && (
            <Card sx={{ mb: 4, boxShadow: 2, bgcolor: 'secondary.50' }}>
              <CardContent sx={{ p: 4 }}>
                <Typography
                  variant="h5"
                  gutterBottom
                  sx={{ fontWeight: 600, color: 'secondary.main' }}
                >
                  Lesson Vocabulary
                </Typography>
                <Divider sx={{ mb: 3 }} />
                <List>
                  {currentUnit.content.lesson_vocabulary.map((vocab: any, idx: number) => (
                    <ListItem
                      key={idx}
                      sx={{
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        py: 2,
                        borderBottom:
                          idx < currentUnit.content.lesson_vocabulary.length - 1 ? 1 : 0,
                        borderColor: 'divider',
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 600, color: 'secondary.main', mb: 1 }}
                      >
                        {vocab.term}
                      </Typography>
                      <Typography variant="body1" color="text.secondary">
                        {vocab.definition}
                      </Typography>
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          )}

        {/* Main Content */}
        {currentUnit.content?.main_content && (
          <Card sx={{ mb: 4, boxShadow: 2 }}>
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <LightbulbIcon sx={{ fontSize: 36, color: 'warning.main', mr: 2 }} />
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                  Lesson Content
                </Typography>
              </Box>
              <Divider sx={{ mb: 3 }} />
              <RichTextEditor
                content={
                  currentUnit.content.main_content.html ||
                  currentUnit.content.main_content.plain_text ||
                  ''
                }
                onChange={() => undefined}
                readOnly={true}
                minHeight={200}
              />
            </CardContent>
          </Card>
        )}

        {/* Activities */}
        {currentUnit.content?.activities && currentUnit.content.activities.length > 0 && (
          <Card sx={{ mb: 4, boxShadow: 2 }}>
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <AssignmentIcon sx={{ fontSize: 36, color: 'success.main', mr: 2 }} />
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                  Activities
                </Typography>
              </Box>
              <Divider sx={{ mb: 3 }} />
              {currentUnit.content.activities.map((activity: any, idx: number) => (
                <Paper key={idx} sx={{ p: 3, mb: 3, border: 2, borderColor: 'success.light' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {activity.title}
                    </Typography>
                    <Chip label={activity.type} size="small" color="success" />
                    {activity.duration_minutes && (
                      <Chip
                        label={`${activity.duration_minutes} min`}
                        size="small"
                        variant="outlined"
                      />
                    )}
                  </Box>
                  <Typography variant="body1" paragraph>
                    {activity.description}
                  </Typography>

                  {activity.instructions && activity.instructions.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                        Instructions:
                      </Typography>
                      <List dense>
                        {activity.instructions.map((instruction: string, i: number) => (
                          <ListItem key={i}>
                            <ListItemText
                              primary={`${i + 1}. ${instruction}`}
                              primaryTypographyProps={{ variant: 'body1' }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}

                  {activity.materials && activity.materials.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                        Materials Needed:
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                        {activity.materials.map((material: string, i: number) => (
                          <Chip key={i} label={material} size="small" variant="outlined" />
                        ))}
                      </Box>
                    </Box>
                  )}
                </Paper>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Summary */}
        {currentUnit.content?.summary && (
          <Card sx={{ mb: 4, boxShadow: 2, bgcolor: 'primary.50' }}>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
                Summary
              </Typography>
              <Divider sx={{ mb: 3 }} />
              <RichTextEditor
                content={
                  currentUnit.content.summary.html || currentUnit.content.summary.plain_text || ''
                }
                onChange={() => undefined}
                readOnly={true}
                minHeight={100}
              />
              {currentUnit.content.summary.key_points &&
                currentUnit.content.summary.key_points.length > 0 && (
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                      Key Takeaways:
                    </Typography>
                    {currentUnit.content.summary.key_points.map((point: string, idx: number) => (
                      <Box key={idx} sx={{ display: 'flex', alignItems: 'start', mb: 1.5 }}>
                        <CheckIcon sx={{ color: 'success.main', mr: 1.5, mt: 0.5, fontSize: 22 }} />
                        <Typography variant="body1">{point}</Typography>
                      </Box>
                    ))}
                  </Box>
                )}
            </CardContent>
          </Card>
        )}
      </Box>
    );
  };

  const renderQuiz = () => {
    if (!currentUnit.test || !currentUnit.test.questions) return null;

    return (
      <Card sx={{ boxShadow: 3 }}>
        <CardContent sx={{ p: 4 }}>
          {/* Tabs for Unit Test and Practice Questions */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Tabs value={quizTab} onChange={(e, newValue) => setQuizTab(newValue)}>
              <Tab icon={<AssignmentIcon />} label="Unit Test" iconPosition="start" />
              <Tab icon={<QuizIcon />} label="Practice Questions" iconPosition="start" />
            </Tabs>
          </Box>

          {/* Unit Test Tab */}
          {quizTab === 0 && (
            <Box>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 1 }}>
                Unit Test
              </Typography>
              <Typography variant="body1" color="text.secondary" paragraph>
                {currentUnit.test.questions.length} questions • {currentUnit.test.total_points}{' '}
                points
              </Typography>
              <Divider sx={{ mb: 4 }} />

              {showQuizResults && (
                <Alert
                  severity={quizScore >= 70 ? 'success' : 'warning'}
                  sx={{ mb: 4 }}
                  icon={quizScore >= 70 ? <CheckIcon /> : undefined}
                >
                  <Typography variant="h6">Your Score: {quizScore}%</Typography>
                  <Typography variant="body2">
                    {quizScore >= 70
                      ? 'Excellent! You passed the test and can move to the next unit.'
                      : 'Review the material and try again to improve your score.'}
                  </Typography>
                </Alert>
              )}

              {currentUnit.test.questions.map((question: any, idx: number) => (
                <Box key={question.question_id} sx={{ mb: 5 }}>
                  <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                    <Chip label={`Question ${idx + 1}`} size="small" color="primary" />
                    <Chip label={question.difficulty} size="small" variant="outlined" />
                    <Chip label={`${question.points} pts`} size="small" />
                  </Box>

                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 500, mb: 3 }}>
                    {question.question_text}
                  </Typography>

                  <FormControl
                    component="fieldset"
                    fullWidth
                    disabled={isTeacherPreview || showQuizResults}
                  >
                    <RadioGroup
                      value={quizAnswers[question.question_id] || ''}
                      onChange={(e) => handleAnswerChange(question.question_id, e.target.value)}
                    >
                      {question.options?.map((option: any) => {
                        const isSelected = quizAnswers[question.question_id] === option.option_id;
                        const isCorrect = option.is_correct;
                        const showCorrectness = showQuizResults || isTeacherPreview;

                        return (
                          <Paper
                            key={option.option_id}
                            sx={{
                              p: 2,
                              mb: 1.5,
                              border: 2,
                              borderColor: showCorrectness
                                ? isCorrect
                                  ? 'success.main'
                                  : isSelected
                                  ? 'error.main'
                                  : 'grey.300'
                                : isSelected
                                ? 'primary.main'
                                : 'grey.300',
                              bgcolor: showCorrectness
                                ? isCorrect
                                  ? 'success.50'
                                  : isSelected
                                  ? 'error.50'
                                  : 'transparent'
                                : 'transparent',
                            }}
                          >
                            <FormControlLabel
                              value={option.option_id}
                              control={<Radio />}
                              label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography variant="body1">{option.text}</Typography>
                                  {showCorrectness && isCorrect && (
                                    <CheckIcon sx={{ color: 'success.main', fontSize: 24 }} />
                                  )}
                                </Box>
                              }
                            />
                          </Paper>
                        );
                      })}
                    </RadioGroup>
                  </FormControl>

                  {(showQuizResults || isTeacherPreview) && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      <Typography variant="body1">
                        <strong>Explanation:</strong> {question.explanation}
                      </Typography>
                    </Alert>
                  )}
                </Box>
              ))}

              {!isTeacherPreview && !showQuizResults && (
                <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={handleSubmitQuiz}
                    //disabled={Object.keys(quizAnswers).length !== currentUnit.test.questions.length}
                    sx={{ px: 6, py: 1.5 }}
                  >
                    Submit Test
                  </Button>
                </Box>
              )}
            </Box>
          )}

          {/* Practice Questions Tab */}
          {quizTab === 1 && moduleId && (
            <PracticeQuestionViewer
              moduleId={moduleId}
              unitId={currentUnit.unit_id}
              unitTitle={currentUnit.title}
            />
          )}
        </CardContent>
      </Card>
    );
  };

  const renderPractice = () => {
    if (!moduleId || !currentUnit) return null;

    return (
      <Card sx={{ boxShadow: 3 }}>
        <CardContent sx={{ p: 4 }}>
          <PracticeQuestionViewer
            moduleId={moduleId}
            unitId={currentUnit.unit_id}
            unitTitle={currentUnit.title}
          />
        </CardContent>
      </Card>
    );
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Button startIcon={<HomeIcon />} onClick={onExit} sx={{ mb: 2 }}>
          Exit Lesson
        </Button>
        <Typography variant="h3" gutterBottom sx={{ fontWeight: 700 }}>
          {moduleTitle}
        </Typography>
        {isTeacherPreview && <Chip label="Teacher Preview Mode" color="secondary" sx={{ mb: 2 }} />}
      </Box>

      {/* Progress Stepper */}
      <Card sx={{ mb: 4, boxShadow: 2 }}>
        <CardContent>
          <Stepper activeStep={currentUnitIndex} alternativeLabel>
            {units.map((unit, index) => (
              <Step key={unit.unit_id} completed={completedUnits.has(index)}>
                <StepLabel>{unit.title}</StepLabel>
              </Step>
            ))}
          </Stepper>
          <Box sx={{ mt: 2 }}>
            <LinearProgress
              variant="determinate"
              value={((currentUnitIndex + (showQuiz ? 0.5 : 0)) / units.length) * 100}
              sx={{ height: 8, borderRadius: 4 }}
            />
          </Box>
        </CardContent>
      </Card>

      {/* Current Unit Title */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
              {showQuiz
                ? `${currentUnit.title} - Test`
                : showPractice
                ? `${currentUnit.title} - Practice`
                : currentUnit.title}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {currentUnit.description}
            </Typography>
          </Box>
          {isTeacherPreview && !showQuiz && moduleId && (
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={() => navigate(`/teacher/lessons/${moduleId}/units`)}
              sx={{ ml: 2 }}
            >
              Edit Content
            </Button>
          )}
        </Box>
      </Box>

      {/* Content or Quiz */}
      {showPractice ? renderPractice() : showQuiz ? renderQuiz() : renderContent()}

      {/* Navigation Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
        <Button
          variant="outlined"
          startIcon={<PrevIcon />}
          onClick={handlePrevUnit}
          disabled={currentUnitIndex === 0 && !showQuiz && !showPractice}
          size="large"
        >
          {showQuiz || showPractice ? 'Back to Content' : 'Previous Unit'}
        </Button>

        <Box sx={{ display: 'flex', gap: 1 }}>
          {!showQuiz && !showPractice && moduleId && (
            <Button
              variant="outlined"
              startIcon={<QuizIcon />}
              onClick={handlePractice}
              size="large"
            >
              Practice Questions
            </Button>
          )}

          {showPractice && (
            <Button
              variant="contained"
              endIcon={<NextIcon />}
              onClick={handleNextUnit}
              size="large"
            >
              {isLastUnit ? 'Complete Module' : 'Next Unit'}
            </Button>
          )}

          {!showPractice && (
            <Button
              variant="contained"
              endIcon={<NextIcon />}
              onClick={handleNextUnit}
              disabled={showQuiz && !showQuizResults && !isTeacherPreview}
              size="large"
            >
              {showQuiz
                ? isLastUnit
                  ? 'Complete Module'
                  : 'Next Unit'
                : hasQuiz
                ? 'Take Quiz'
                : isLastUnit
                ? 'Complete Module'
                : 'Next Unit'}
            </Button>
          )}
        </Box>
      </Box>
    </Container>
  );
};
